import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Camera, Upload, X } from "lucide-react";
import FileThumb from "@/components/visit/FileThumb";
import { PHOTO_TAGS } from "@/lib/visitSchema";

function PendingThumb({ photo, onRemove }) {
  return (
    <div className="relative aspect-square rounded-md overflow-hidden border border-amber-200 bg-slate-50">
      <img src={photo.previewUrl} alt={photo.filename} className="w-full h-full object-cover" />
      <button type="button" onClick={() => onRemove(photo.local_id)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 shadow hover:bg-red-50" aria-label="Remove pending photo" data-testid={`pending-remove-${photo.local_id}`}>
        <X className="h-3 w-3 text-red-600" />
      </button>
      <span className="absolute bottom-1 left-1 bg-amber-500 text-white text-[9px] px-1.5 py-0.5 rounded-full">Pending</span>
    </div>
  );
}

const STAGE_LABEL = { compressing: "Compressing", uploading: "Uploading", syncing: "Syncing" };

function UploadProgress({ progress, section, testId }) {
  if (!progress || progress.section !== section) return null;
  return (
    <div className="mb-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`${testId}-progress`}>
      <div className="flex items-center justify-between text-[11px] text-slate-600 mb-1">
        <span>{STAGE_LABEL[progress.stage] || "Working"} photo {progress.index} of {progress.total}…</span>
        <span className="num">{progress.pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
        <div className="h-full bg-emerald-500 transition-[width] duration-200" style={{ width: `${progress.stage === "compressing" ? 5 : Math.max(5, progress.pct)}%` }} />
      </div>
    </div>
  );
}

function PickButton({ icon: Icon, label, busy, inputProps, dashed = false }) {
  return (
    <label className={`inline-flex items-center gap-2 px-3 py-2 border ${dashed ? "border-dashed" : "bg-white"} border-slate-300 rounded-md text-sm ${busy ? "opacity-60 cursor-wait" : "cursor-pointer hover:bg-slate-50"}`}>
      <Icon className="h-4 w-4" /> {busy ? "Uploading…" : label}
      <input type="file" className="hidden" disabled={busy} {...inputProps} />
    </label>
  );
}

function UploadedGrid({ section, files, pending, uploads, showTags, testId }) {
  if (!files.length && !pending.length) return null;
  return (
    <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
      {files.map((f, i) => (
        <FileThumb key={f.file_id} fileId={f.file_id} tag={showTags ? f.tag : null} canPreview index={i} tagOptions={PHOTO_TAGS}
          onRemove={(id) => uploads.removeFile(section, id)} onRetag={showTags ? uploads.retag : undefined} testId={`${testId}-file-${i}`} />
      ))}
      {pending.map((p) => <PendingThumb key={p.local_id} photo={p} onRemove={uploads.removePending} />)}
    </div>
  );
}

/* Capture / pick files for one inspection section, with thumbnails for uploaded + queued files. */
export default function PhotoUploader({ section, label, hint, files, uploads, showTags = false, accept = "image/*", min = 1, testId }) {
  const [tag, setTag] = useState("front");
  const pending = uploads.pendingFor(section);
  const pick = (e) => {
    uploads.capture(section, Array.from(e.target.files || []), showTags ? tag : "other");
    e.target.value = "";
  };
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <Label className="text-xs">{label}</Label>
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${files.length >= min ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`${testId}-count`}>
          {files.length} uploaded{pending.length ? ` · ${pending.length} pending` : ""}
        </span>
      </div>
      {hint && <p className="text-xs text-slate-500 mb-2">{hint}</p>}
      <UploadProgress progress={uploads.progress} section={section} testId={testId} />
      <div className="flex flex-wrap items-center gap-2">
        {showTags && (
          <select value={tag} onChange={(e) => setTag(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm" aria-label="Tag next photos as" data-testid={`${testId}-tag-select`}>
            {PHOTO_TAGS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        )}
        <PickButton icon={Camera} label="Take photo" busy={uploads.uploading} inputProps={{ accept: "image/*", capture: "environment", onChange: pick, "data-testid": `${testId}-camera` }} />
        <PickButton icon={Upload} label="Choose files" busy={uploads.uploading} dashed inputProps={{ accept, multiple: true, onChange: pick, "data-testid": `${testId}-input` }} />
      </div>
      <UploadedGrid section={section} files={files} pending={pending} uploads={uploads} showTags={showTags} testId={testId} />
    </div>
  );
}
